import { Control, Controller, UseFieldArrayRemove } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { DatePicker } from '@/components/ui/date-picker';
import { Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import type { Application } from '@/types/database.types';
import type { PayScheduleType } from '@/types/database/common-types';

export interface AdditionalScheduleProps {
  index: number;
  control: Control<Partial<Application>>;
  remove: UseFieldArrayRemove;
}

export const AdditionalSchedule = ({ index, control, remove }: AdditionalScheduleProps) => {
  return (
    <div className="border p-4 rounded-md relative">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2 text-destructive"
        onClick={() => remove(index)}
      >
        <Trash2 size={16} />
      </Button>
      
      <h4 className="text-lg font-medium mb-4">Additional Schedule {index + 1}</h4>
      
      <div className="space-y-6">
        <div className="space-y-4">
          <Label>Pay Schedule Type</Label>
          <Controller
            name={`additional_schedules.${index}.schedule_type`}
            control={control}
            render={({ field }) => (
              <RadioGroup
                value={field.value as string}
                onValueChange={(value) => field.onChange(value as PayScheduleType)}
                className="flex flex-col space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Weekly" id={`weekly-${index}`} />
                  <Label htmlFor={`weekly-${index}`} className="cursor-pointer">Weekly</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Bi-Weekly" id={`bi-weekly-${index}`} />
                  <Label htmlFor={`bi-weekly-${index}`} className="cursor-pointer">Bi-Weekly</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Semi-Monthly" id={`semi-monthly-${index}`} />
                  <Label htmlFor={`semi-monthly-${index}`} className="cursor-pointer">Semi-Monthly</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Monthly" id={`monthly-${index}`} />
                  <Label htmlFor={`monthly-${index}`} className="cursor-pointer">Monthly</Label>
                </div>
              </RadioGroup>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor={`pay_period_start-${index}`}>Period Start</Label>
            <Controller 
              name={`additional_schedules.${index}.pay_period_start`}
              control={control}
              render={({ field }) => (
                <DatePicker
                  id={`pay_period_start-${index}`}
                  date={field.value ? new Date(field.value) : undefined}
                  onSelect={(date) => field.onChange(date ? date.toISOString() : undefined)}
                  placeholder="Select date"
                />
              )}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor={`pay_period_end-${index}`}>Period End</Label>
            <Controller 
              name={`additional_schedules.${index}.pay_period_end`}
              control={control}
              render={({ field }) => (
                <DatePicker
                  id={`pay_period_end-${index}`}
                  date={field.value ? new Date(field.value) : undefined}
                  onSelect={(date) => field.onChange(date ? date.toISOString() : undefined)}
                  placeholder="Select date"
                />
              )}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor={`next_check_date-${index}`}>Pay Date</Label>
            <Controller 
              name={`additional_schedules.${index}.next_check_date`}
              control={control}
              render={({ field }) => (
                <DatePicker
                  id={`next_check_date-${index}`}
                  date={field.value ? new Date(field.value) : undefined}
                  onSelect={(date) => field.onChange(date ? date.toISOString() : undefined)}
                  placeholder="Select date"
                />
              )}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor={`description-${index}`}>Description</Label>
          <Controller
            name={`additional_schedules.${index}.description`}
            control={control}
            render={({ field }) => (
              <Input
                type="text"
                id={`description-${index}`}
                className="w-full p-2 border rounded"
                placeholder="E.g., For part-time employees only"
                {...field}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};
